import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Users, 
  Plus, 
  UserPlus, 
  Crown, 
  TrendingUp, 
  Trophy,
  Target,
  Shield,
  Flame,
  CheckCircle,
  Star,
  Copy,
  Check,
  LogOut,
  Mail,
  ChevronRight,
  Sparkles,
  Zap,
  Award
} from "lucide-react";
import { AppLayout } from "./DashboardPage";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Milestone icon mapping
const MILESTONE_ICONS = {
  'check-circle': CheckCircle,
  'flame': Flame,
  'fire': Flame,
  'trending-up': TrendingUp,
  'star': Star,
  'trophy': Trophy,
  'target': Target,
  'shield': Shield,
  'shield-check': Shield,
  'users': Users,
  'plus-circle': Plus,
  'zap': Zap,
  'award': Award
};

// Get score color based on value
function getScoreColor(score) {
  if (score >= 75) return 'text-[#1a4d3e]';
  if (score >= 50) return 'text-[#1a4d3e]/80';
  if (score >= 25) return 'text-amber-600';
  return 'text-slate-500';
}

// Get score background based on value
function getScoreBg(score) {
  if (score >= 75) return 'bg-[#1a4d3e]/10';
  if (score >= 50) return 'bg-[#1a4d3e]/5';
  if (score >= 25) return 'bg-amber-50 dark:bg-amber-900/20';
  return 'bg-slate-100 dark:bg-slate-800';
}

// Format relative time
function formatRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' });
}

// Circle Score Ring Component
function ScoreRing({ score, size = 'md', showLabel = true }) {
  const sizes = {
    sm: { width: 48, stroke: 4, text: 'text-base' },
    md: { width: 64, stroke: 5, text: 'text-xl' },
    lg: { width: 80, stroke: 6, text: 'text-2xl' }
  };
  
  const { width, stroke, text } = sizes[size];
  const radius = (width - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = score ? (score / 100) * circumference : 0;
  
  return (
    <div className="relative" style={{ width, height: width }}>
      <svg className="-rotate-90" width={width} height={width}>
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={stroke}
          fill="none"
          className="text-border"
        />
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={stroke}
          fill="none"
          className={getScoreColor(score || 0)}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
        />
      </svg>
      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-heading font-semibold ${text} ${getScoreColor(score || 0)}`}>
            {score ?? '—'}
          </span>
        </div>
      )}
    </div>
  );
}

// Member Card Component
function MemberCard({ member, isCompact = false }) {
  const IconComponent = member.is_owner ? Crown : Users;
  const streak = member.weekly_close_streak || 0;
  
  if (isCompact) {
    return (
      <div className={`flex items-center justify-between p-4 rounded-xl border ${
        member.is_you ? 'bg-[#1a4d3e]/5 border-[#1a4d3e]/20' : 'bg-card border-border'
      }`}>
        <div className="flex items-center gap-3">
          <ScoreRing score={member.calm_score} size="sm" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">{member.name}</span>
              {member.is_you && (
                <span className="text-xs text-[#1a4d3e] bg-[#1a4d3e]/10 px-1.5 py-0.5 rounded">You</span>
              )}
              {member.is_owner && (
                <Crown className="w-3.5 h-3.5 text-amber-500" />
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{member.calm_level || 'Building'}</span>
              {streak > 0 && (
                <span className="flex items-center gap-1">
                  <Flame className="w-3 h-3 text-orange-500" />
                  {streak}w
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`p-5 rounded-xl border ${
      member.is_you ? 'bg-[#1a4d3e]/5 border-[#1a4d3e]/20' : 'bg-card border-border'
    }`} data-testid={`member-card-${member.household_id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getScoreBg(member.calm_score || 0)}`}>
            <IconComponent className={`w-5 h-5 ${getScoreColor(member.calm_score || 0)}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-foreground">{member.name}</h3>
              {member.is_you && (
                <span className="text-xs text-[#1a4d3e] bg-[#1a4d3e]/10 px-1.5 py-0.5 rounded">You</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {member.is_owner ? 'Circle Owner' : 'Member'}
            </p>
          </div>
        </div>
        <ScoreRing score={member.calm_score} size="md" />
      </div>
      
      {/* Streak Display */}
      {streak > 0 && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <Flame className="w-4 h-4 text-orange-500" />
          <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
            {streak} week streak
          </span>
        </div>
      )}
      
      {/* Recent milestones */}
      {member.recent_milestones && member.recent_milestones.length > 0 && (
        <div className="pt-3 border-t border-border/50">
          <p className="text-xs text-muted-foreground mb-2">Recent</p>
          <div className="space-y-1.5">
            {member.recent_milestones.slice(0, 2).map((ms) => {
              const Icon = MILESTONE_ICONS[ms.icon] || Star;
              return (
                <div key={ms.milestone_id} className="flex items-center gap-2 text-sm">
                  <Icon className="w-3.5 h-3.5 text-[#1a4d3e]" />
                  <span className="text-muted-foreground truncate">{ms.description}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Activity Feed Item
function ActivityItem({ activity }) {
  const Icon = MILESTONE_ICONS[activity.icon] || Star;
  const emoji = activity.emoji || '';
  
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0" data-testid="activity-item">
      <div className="w-8 h-8 rounded-lg bg-[#1a4d3e]/10 flex items-center justify-center flex-shrink-0">
        {emoji ? (
          <span className="text-base">{emoji}</span>
        ) : (
          <Icon className="w-4 h-4 text-[#1a4d3e]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">
          <span className="font-medium">{activity.household_name}</span>
          {activity.is_you && <span className="text-xs text-[#1a4d3e] ml-1">(you)</span>}
          {' '}{activity.description}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatRelativeTime(activity.achieved_at)}
        </p>
      </div>
    </div>
  );
}

// Create Circle Form
function CreateCircleForm({ onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/circles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), description: description.trim() || null })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to create circle');
      }
      
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Circle Name
        </label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Family Finance Circle"
          className="w-full"
          data-testid="circle-name-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Description <span className="text-muted-foreground">(optional)</span>
        </label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., Supporting each other on our journey"
          className="w-full"
        />
      </div>
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      <Button 
        type="submit" 
        disabled={loading || !name.trim()}
        className="w-full bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
        data-testid="create-circle-btn"
      >
        {loading ? 'Creating...' : 'Create Circle'}
      </Button>
    </form>
  );
}

// Join Circle Form
function JoinCircleForm({ onJoined }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/circles/join?code=${code.trim().toUpperCase()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Invalid invite code');
      }
      
      onJoined();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Invite Code
        </label>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Enter 8-character code"
          className="w-full font-mono tracking-wider"
          maxLength={8}
          data-testid="invite-code-input"
        />
      </div>
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      <Button 
        type="submit" 
        disabled={loading || code.length < 8}
        className="w-full bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
        data-testid="join-circle-btn"
      >
        {loading ? 'Joining...' : 'Join Circle'}
      </Button>
    </form>
  );
}

// Invite Modal
function InviteModal({ circleId, circleName, onClose }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/circles/${circleId}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), message: message.trim() || null })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to send invite');
      }
      
      const data = await res.json();
      setInvite(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const copyCode = () => {
    if (invite?.invite_code) {
      navigator.clipboard.writeText(invite.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-card rounded-xl border border-border max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-heading text-lg font-medium mb-4">
          Invite to {circleName}
        </h3>
        
        {invite ? (
          <div className="space-y-4">
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-[#1a4d3e] mx-auto mb-3" />
              <p className="text-foreground mb-1">Invite Created!</p>
              <p className="text-sm text-muted-foreground">
                Share this code with {email}
              </p>
            </div>
            
            <div className="bg-[#1a4d3e]/5 rounded-lg p-4 flex items-center justify-between">
              <span className="font-mono text-2xl tracking-wider text-[#1a4d3e]">
                {invite.invite_code}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyCode}
                className="text-[#1a4d3e]"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              Code expires in 7 days
            </p>
            
            <Button onClick={onClose} className="w-full" variant="outline">
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="friend@example.com"
                className="w-full"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Personal Message <span className="text-muted-foreground">(optional)</span>
              </label>
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Join our circle!"
                className="w-full"
              />
            </div>
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !email.trim()}
                className="flex-1 bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
                data-testid="send-invite-btn"
              >
                {loading ? 'Sending...' : 'Send Invite'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Empty State - No Circle
function NoCircleState({ onCreateClick, onJoinClick }) {
  const [mode, setMode] = useState(null); // null, 'create', 'join'
  
  return (
    <div className="max-w-lg mx-auto text-center py-12 px-6">
      <div className="w-16 h-16 rounded-2xl bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-6">
        <Users className="w-8 h-8 text-[#1a4d3e]" />
      </div>
      
      <h2 className="font-heading text-xl font-semibold mb-2">
        Join a Household Circle
      </h2>
      <p className="text-muted-foreground mb-8">
        Circles let trusted households encourage each other's financial stability while keeping private details private.
      </p>
      
      {mode === null ? (
        <div className="space-y-3">
          <Button 
            onClick={() => setMode('create')}
            className="w-full bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
            data-testid="start-create-circle-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create a Circle
          </Button>
          <Button 
            onClick={() => setMode('join')}
            variant="outline"
            className="w-full"
            data-testid="start-join-circle-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Join with Invite Code
          </Button>
        </div>
      ) : mode === 'create' ? (
        <div className="text-left">
          <button 
            onClick={() => setMode(null)}
            className="text-sm text-muted-foreground mb-4 hover:text-foreground"
          >
            ← Back
          </button>
          <CreateCircleForm onCreated={onCreateClick} />
        </div>
      ) : (
        <div className="text-left">
          <button 
            onClick={() => setMode(null)}
            className="text-sm text-muted-foreground mb-4 hover:text-foreground"
          >
            ← Back
          </button>
          <JoinCircleForm onJoined={onJoinClick} />
        </div>
      )}
      
      {/* Privacy note */}
      <div className="mt-8 p-4 bg-[#1a4d3e]/5 rounded-lg text-left">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Privacy Protected:</span>{' '}
          Circle members can only see your household name, Calm Score, and milestones. 
          Your bank balances, income amounts, and transactions remain completely private.
        </p>
      </div>
    </div>
  );
}

// Main Circle Page
export default function CirclePage() {
  const { household } = useAuth();
  const [loading, setLoading] = useState(true);
  const [circleData, setCircleData] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [leaving, setLeaving] = useState(false);
  
  const fetchCircle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/circles`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setCircleData(data.circle);
      }
    } catch (error) {
      console.error('Failed to fetch circle:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (household) {
      fetchCircle();
    }
  }, [household]);
  
  const handleLeaveCircle = async () => {
    if (!circleData || !window.confirm('Are you sure you want to leave this circle?')) return;
    
    setLeaving(true);
    try {
      const res = await fetch(`${API_URL}/api/circles/${circleData.circle_id}/leave`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        setCircleData(null);
      }
    } catch (error) {
      console.error('Failed to leave circle:', error);
    } finally {
      setLeaving(false);
    }
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-[#1a4d3e] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }
  
  if (!circleData) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-8" data-testid="circle-page">
          <NoCircleState 
            onCreateClick={fetchCircle}
            onJoinClick={fetchCircle}
          />
        </div>
      </AppLayout>
    );
  }
  
  const { members, stats, recent_activity } = circleData;
  
  return (
    <AppLayout>
      <div className="p-6 lg:p-8 max-w-6xl" data-testid="circle-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-foreground mb-1">
              {circleData.name}
            </h1>
            <p className="text-muted-foreground text-sm">
              {circleData.description || 'Supporting each other on our financial journey'}
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowInviteModal(true)}
              className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
              data-testid="invite-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite
            </Button>
            <Button 
              variant="outline"
              onClick={handleLeaveCircle}
              disabled={leaving}
              data-testid="leave-circle-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Leave
            </Button>
          </div>
        </div>
        
        {/* Circle Stats */}
        <section className="mb-8" data-testid="circle-stats">
          <div className="bg-gradient-to-br from-[#1a4d3e]/5 to-[#1a4d3e]/10 rounded-xl border border-[#1a4d3e]/20 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              {/* Average Score */}
              <div className="flex items-center gap-4">
                <ScoreRing score={stats.average_score} size="lg" />
                <div>
                  <p className="text-sm text-muted-foreground">Circle Average</p>
                  <p className="font-heading text-lg font-medium text-foreground">
                    Calm Score
                  </p>
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex-1 grid grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-semibold text-foreground">{stats.member_count}</p>
                  <p className="text-xs text-muted-foreground">Members</p>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-semibold text-[#1a4d3e]">{stats.highest_score || '—'}</p>
                  <p className="text-xs text-muted-foreground">Highest Score</p>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-semibold text-foreground">{stats.total_milestones_this_week}</p>
                  <p className="text-xs text-muted-foreground">Milestones This Week</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Members */}
          <div className="lg:col-span-2 space-y-4" data-testid="circle-members">
            <h2 className="font-heading text-base font-medium text-foreground flex items-center gap-2">
              <Users className="w-4 h-4 text-[#1a4d3e]" />
              Circle Members
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {members.map((member) => (
                <MemberCard key={member.household_id} member={member} />
              ))}
            </div>
          </div>
          
          {/* Activity Feed */}
          <div className="space-y-4" data-testid="circle-activity">
            <h2 className="font-heading text-base font-medium text-foreground flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#1a4d3e]" />
              Recent Activity
            </h2>
            <div className="bg-card rounded-xl border border-border p-4">
              {recent_activity && recent_activity.length > 0 ? (
                <div className="divide-y divide-border/50">
                  {recent_activity.map((activity) => (
                    <ActivityItem key={activity.milestone_id} activity={activity} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Sparkles className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No activity yet this week</p>
                </div>
              )}
            </div>
            
            {/* Privacy Note */}
            <div className="p-4 bg-[#1a4d3e]/5 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <Shield className="w-3.5 h-3.5 inline mr-1 text-[#1a4d3e]" />
                <span className="font-medium text-foreground">Privacy:</span>{' '}
                Only Calm Scores and milestones are shared. Balances and transactions stay private.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal
          circleId={circleData.circle_id}
          circleName={circleData.name}
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </AppLayout>
  );
}
